import { Booking } from "@api";
import { BlockOutlined } from "@mui/icons-material";
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, Stack, TextField, Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { titleEnumToString } from "sprungkraft/api/api";
import { parseError } from "sprungkraft/helper/helper_functions";
import { useStore } from "sprungkraft/state/state";
import AdminPage from "sprungkraft/widgets/admin_page";
import FormattedDateTime from "sprungkraft/widgets/formatted_date_time";
import LoadingWidget from "sprungkraft/widgets/loading_widget";
import MoveBookingToCourseWidget from "./move_booking_to_course";

export interface BookingDetailsProps {
    booking: Booking;
    onRefresh: () => void;
}

enum Progress {
    Idle = "idle",
    Done = "done",
    Loading = "loading",
}

const StornoDialog = ({
    open,
    onClose,
    onConfirm,
}: {
    open: boolean;
    onClose: () => void;
    onConfirm: (refundAmount: number | null) => void;
}) => {
    const [refundInput, setRefundInput] = useState('');

    const handleConfirm = () => {
        const parsed = parseFloat(refundInput.replace(',', '.'));
        onConfirm(isNaN(parsed) ? null : parsed);
        setRefundInput('');
    };

    const handleClose = () => {
        setRefundInput('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Buchung stornieren?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Die Buchung wird als storniert markiert und bleibt für die Buchungsliste erhalten.
                </Typography>
                <TextField
                    label="Rückerstattungsbetrag (€)"
                    placeholder="z.B. 45.00"
                    value={refundInput}
                    onChange={(e) => setRefundInput(e.target.value)}
                    type="text"
                    inputMode="decimal"
                    size="small"
                    fullWidth
                    helperText="Optional – leer lassen wenn kein Betrag zurückzuerstatten ist"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit">Nein, abbrechen</Button>
                <Button onClick={handleConfirm} color="warning" variant="contained">
                    Ja, stornieren
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const BookingDetails = ({ booking, onRefresh }: BookingDetailsProps) => {
    const { api: { bookingApi } } = useStore();
    const [confirmationEmailProgress, setConfirmationEmailProgress] = useState<Progress>(Progress.Idle);
    const [stornoDialogOpen, setStornoDialogOpen] = useState(false);
    const [stornoError, setStornoError] = useState<string | null>(null);

    const onResendBookingConfirmation = () => {
        setConfirmationEmailProgress(Progress.Loading);
        bookingApi.sendConfirmationEmail({ uuid: booking.uuid }).subscribe(() => setConfirmationEmailProgress(Progress.Done));
    };

    const handleStornoConfirm = (refundAmount: number | null) => {
        setStornoDialogOpen(false);
        bookingApi.cancelBooking({
            uuid: booking.uuid,
            cancelBooking: { refund_amount: refundAmount ?? undefined },
        }).subscribe({
            next: () => onRefresh(),
            error: (err) => setStornoError(parseError(err)),
        });
    };

    return (
        <Stack spacing={2}>
            {/* Storniert badge */}
            {booking.cancelled && (
                <Alert severity="warning" icon={<BlockOutlined />}>
                    <strong>Storniert</strong>
                    {booking.refund_amount != null && (
                        <> – Rückerstattungsbetrag: <strong>{booking.refund_amount.toFixed(2)} €</strong></>
                    )}
                </Alert>
            )}

            <Box>
                <Typography variant="h6">Buchungsdetails</Typography>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Buchungszeitpunkt</Typography>
                        <Typography><FormattedDateTime value={booking.timestamp} /></Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Kurs</Typography>
                        <Typography>Titel: {booking.course.title}</Typography>
                        <Typography>Wochentag: {booking.course_offer.weekday_time_information}</Typography>
                        <Typography>Termine: {booking.course_offer.dates_information}</Typography>
                    </Grid>
                </Grid>
            </Box>
            <Box>
                <Typography variant="h6">Personendetails</Typography>
                <Grid container sx={{ marginBottom: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Vorname</Typography>
                        <Typography>{booking.participant.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Nachname</Typography>
                        <Typography>{booking.participant.surname}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">E-Mail</Typography>
                        <Typography>{booking.participant.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Telefon</Typography>
                        <Typography>{booking.participant.phone_number}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Adresse</Typography>
                        <Typography>{booking.participant.street} {booking.participant.house_number}</Typography>
                        <Typography>{booking.participant.zip_code} {booking.participant.city}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Geburtsdatum</Typography>
                        <Typography>{booking.participant.birth_date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption">Geschlecht</Typography>
                        <Typography>{titleEnumToString(booking.participant.title)}</Typography>
                    </Grid>
                </Grid>
            </Box>
            <Stack alignItems="start" spacing={1}>
                <Typography variant="h6">Aktionen</Typography>
                {confirmationEmailProgress === Progress.Done && (
                    <Alert severity="success" onClose={() => setConfirmationEmailProgress(Progress.Idle)}>
                        Buchungsbestätigung wurde erneut gesendet.
                    </Alert>
                )}
                {stornoError && (
                    <Alert severity="error" onClose={() => setStornoError(null)}>{stornoError}</Alert>
                )}
                <Button
                    disabled={confirmationEmailProgress !== Progress.Idle}
                    variant="outlined"
                    onClick={onResendBookingConfirmation}
                >
                    Buchungsbestätigung erneut senden
                </Button>
                {!booking.cancelled && (
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<BlockOutlined />}
                        onClick={() => setStornoDialogOpen(true)}
                    >
                        Storno
                    </Button>
                )}
            </Stack>
            <Box>
                <Typography variant="h6">Buchung verschieben</Typography>
                <MoveBookingToCourseWidget booking={booking} onRefresh={onRefresh} />
            </Box>

            <StornoDialog
                open={stornoDialogOpen}
                onClose={() => setStornoDialogOpen(false)}
                onConfirm={handleStornoConfirm}
            />
        </Stack>
    );
};


const AdminBookingDetails = () => {
    const { uuid } = useParams();
    const [booking, setBooking] = useState<Booking | null>(null);
    const { api: { bookingApi } } = useStore()
    const [effect, setEffect] = useState(false);

    const updateEffect = () => setEffect(!effect);

    useEffect(() => {
        bookingApi.getBooking({ uuid: uuid! }).subscribe({ next: setBooking });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effect])

    return <AdminPage>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }}>
            <Typography variant="h5">Buchungsdetails</Typography>
            {booking?.cancelled && (
                <Chip label="Storniert" color="warning" size="small" icon={<BlockOutlined />} />
            )}
        </Stack>
        {booking ? <BookingDetails booking={booking} onRefresh={updateEffect} /> : <LoadingWidget />}
    </AdminPage>
}

export default AdminBookingDetails;
